<template>
    <v-app class="ppv2-bg-white dark:ppv2-bg-dark">
        <v-container fluid>
            <CustomButtonSave @click="handleSaveAndExit" v-if="![1, 5].includes(step) && !complited" :loading="loading" />
            <div
                v-if="complited && step !== 5"
                :style="{
                    position: 'absolute',
                    top: '25px',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    gap: '8px',
                    right: normalizeSize(50, true),
                    zIndex: 10,
                }"
                @click="redirectToList"
            >
                <ChevronLeft color="#505d92" :size="20" />
                <span class="ppv2-text-black ppv2-text-opacity-70" :style="{ fontSize: '16px' }">
                    {{ $t('experience.backToExperiences') }}
                </span>
            </div>
            <v-row class="ppv2-flex ppv2-h-full">
                <!-- Step headers on the left -->
                <v-col
                    v-if="step !== 5"
                    class="col col-md-2 col-sm-3 col-4 stepper-container"
                    :class="[{ 'stepper-container-edit !ppv2-pt-[40px]': complited }, { 'mt-40': !complited }]"
                >
                    <div
                        v-for="(s, index) in visibleSteps"
                        :key="s.id"
                        :class="[
                            'step-item',
                            {
                                disabled: !complited && s.id > highestValidatedStep,
                                completed: completedSteps.includes(s.id) && !complited,
                                completedMenu: completedSteps.includes(s.id) && complited,
                                active: step === s.id && !complited,
                                activeMenu: step === s.id && complited,
                            },
                            { '!ppv2-min-h-[44px] !ppv2-items-center': complited },
                        ]"
                        @click="handleStepClick(s.id)"
                    >
                        <!-- Circle with checkmark or number -->
                        <div class="circle" v-if="!complited">
                            <Check v-if="completedSteps.includes(s.id)" color="white" :size="14" />
                        </div>

                        <!-- Line between steps -->
                        <div
                            v-if="index < visibleSteps.length - 1 && !complited"
                            class="line"
                            :class="{ completed: completedSteps.includes(s.id) }"
                        ></div>

                        <!-- Text -->
                        <div class="d-flex flex-column justify-content-center" :style="{ marginLeft: normalizeSize(22, true) }">
                            <div v-if="!complited" class="step-title ppv2-text-black" :style="{ fontSize: '11px' }">
                                ÉTAPE {{ getStepDisplayNumber(s.id) }}
                            </div>
                            <div
                                class="step-subtitle ppv2-text-black"
                                :class="{ '!ppv2-opacity-100': complited }"
                                :style="{ fontSize: complited ? sizes.subtitleMenu : sizes.subtitle }"
                            >
                                {{ s.subtitle }}
                            </div>
                        </div>
                    </div>
                </v-col>

                <!-- Step content on the right -->
                <v-col
                    :class="{
                        'col col-md-10 col-sm-9 col-8 step-content px-0 !ppv2-pt-[46px]': step !== 5,
                        'col col-md-12 col-sm-12 col-12': step === 5,
                    }"
                >
                    <v-stepper class="ppv2-bg-white dark:ppv2-bg-dark" v-model="step">
                        <v-stepper-content v-for="s in allSteps" :key="s.id" :step="s.id" class="mr-0 px-0 ppv2-h-full">
                            <div class="mb-4 ppv2-bg-white dark:ppv2-bg-dark">
                                <component
                                    :is="s.component"
                                    :ref="'step' + s.id"
                                    :step="s"
                                    :sizes="sizes"
                                    :id="isEditMode ? null : treatmentId"
                                    :complited="complited"
                                    @next-step="handleNextStep(s.id)"
                                    @previous-step="goToPrevious(s.id - 1)"
                                    @save-and-exit="handleSaveAndExit"
                                >
                                </component>
                            </div>
                        </v-stepper-content>
                    </v-stepper>
                </v-col>
            </v-row>
        </v-container>

        <!-- Confirm navigation dialog -->
        <CustomDialog
            v-model="showConfirmDialog"
            :defaultTitle="$t('experience.titleClosePopup')"
            customClassTitle="poppinsSemiBold"
            noScroll
        >
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('experience.descriptionClosePopup') }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    type="button"
                    variant="outline"
                    @click="_onConfirmDialog"
                    customHeight="43"
                    customClass="!ppv2-min-h-[40px]"
                >
                    {{ $t('experience.btnLeave') }}
                </BaseButton>
                <BaseButton
                    type="button"
                    variant="primary"
                    @click="_onCancelDialog"
                    customHeight="43"
                    customClass="!ppv2-min-h-[40px]"
                    :isLargeBtn="true"
                >
                    {{ $t('experience.btnStay') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </v-app>
</template>

<script>
import Step1 from './components/Step1.vue'
import Step2 from './components/Step2.vue'
import Step3 from './components/Step3.vue'
import Step5 from './components/Step5.vue'
import Step6 from '../Experience/components/Step8.vue'
import { theme } from '../../../utils/theme'
import normalizeSize from '../../../utils/normalizeSize'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import CustomButtonSave from '../../Shared/CustomButtonSave.vue'
import { Check, ChevronLeft } from 'lucide-vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import { EventBus } from '../../../eventBus'
import { mapGetters, mapActions } from 'vuex'

export default {
    name: 'TreatmentCreate',
    components: { CustomButtonSave, ChevronLeft, CustomDialog, BaseButton, Check },
    props: {
        id: {
            type: [Number, String],
            default: null,
        },
        country_vat_rates: {
            type: Array,
            default: () => [],
        },
        prestations_with_prices_times: {
            type: Array,
            default: () => [],
        },
        treatment_data: {
            type: Object,
            default: null,
        },
        complited: {
            type: [Boolean],
            default: false,
        },
    },
    data() {
        return {
            step: 1,
            completedSteps: [],
            highestValidatedStep: 1,
            isSaving: false,
            showConfirmDialog: false,
            _pendingConfirm: null,
            _pendingCancel: null,
            allSteps: [
                {
                    id: 1,
                    subtitle: this.$t('treatment.step1.subtitle') || 'Informations générales',
                    content: this.$t('treatment.step1.content') || 'Créer un forfait',
                    component: Step1,
                },
                {
                    id: 2,
                    subtitle: this.$t('treatment.step2.subtitle') || 'Tarification',
                    content: this.$t('treatment.step2.content') || 'Définissez votre prix',
                    component: Step2,
                },
                {
                    id: 3,
                    subtitle: this.$t('treatment.step3.subtitle') || 'Paramètres',
                    content: this.$t('treatment.step3.content') || 'Configuration heures',
                    component: Step3,
                },
                {
                    id: 4,
                    subtitle: this.$t('treatment.step5.subtitle') || 'Options',
                    content: this.$t('treatment.step5.content') || 'Options de visibilité',
                    component: Step5,
                },
                {
                    id: 5,
                    subtitle: 'Finish',
                    content: '',
                    component: Step6,
                },
            ],
        }
    },
    computed: {
        ...mapGetters('treatment', ['treatmentId', 'isEditMode', 'getStepValidation', 'loading', 'currentStep']),
        sizes() {
            return {
                title: this.normalizeSize(32, true),
                subtitle: this.normalizeSize(13, true),
                subtitleMenu: this.normalizeSize(12, true),
                formStep: this.normalizeSize(15, true),
            }
        },
        visibleSteps() {
            // Show only steps 1-4 in the sidebar (step 5 is finish screen)
            return this.allSteps.slice(0, 4)
        },
        canEdit() {
            return this.completedSteps.length >= 3
        },
    },
    created() {
        // Initialize store with props data
        this.setCountryVatRates(this.country_vat_rates)
        this.setAvailablePrestations(this.prestations_with_prices_times)

        // If editing, load treatment data and set completed steps
        if (this.id) {
            this.loadTreatmentData()
            // All steps are completed in edit mode
            this.completedSteps = [1, 2, 3, 4]
            this.highestValidatedStep = 4
            this.step = 3 // Paramètres — étape la plus utilisée en édition forfait
        } else if (this.treatment_data && this.treatment_data.steps) {
            // Resume creation from saved step
            this.loadTreatmentData()
            this.step = this.treatment_data.steps
            this.updateCompletedSteps(this.treatment_data.steps)
        } else {
            // Reset store for new treatment
            this.resetStore()
        }
    },
    methods: {
        ...mapActions('treatment', [
            'setCountryVatRates',
            'setAvailablePrestations',
            'loadTreatment',
            'saveTreatment',
            'resetStore',
            'setCurrentStep',
            'saveStepProgress',
        ]),
        normalizeSize,
        normalizeFontSize,
        _confirmLeave(onConfirm, onCancel) {
            this._pendingConfirm = onConfirm
            this._pendingCancel = onCancel || null
            this.showConfirmDialog = true
        },
        _onConfirmDialog() {
            this.showConfirmDialog = false
            if (typeof this._pendingConfirm === 'function') {
                this._pendingConfirm()
            }
            this._pendingConfirm = null
            this._pendingCancel = null
        },
        _onCancelDialog() {
            this.showConfirmDialog = false
            if (typeof this._pendingCancel === 'function') {
                this._pendingCancel()
            }
            this._pendingConfirm = null
            this._pendingCancel = null
        },

        getStepDisplayNumber(stepId) {
            return stepId
        },

        updateCompletedSteps(currentStep) {
            this.completedSteps = []
            for (let i = 1; i < currentStep; i++) {
                this.completedSteps.push(i)
            }
            this.highestValidatedStep = currentStep
        },

        async loadTreatmentData() {
            if (this.treatment_data) {
                // Use pre-loaded data from blade
                this.$store.commit('treatment/LOAD_TREATMENT', this.treatment_data)
            } else if (this.id) {
                // Fetch from API
                await this.loadTreatment(this.id)
            }
        },

        async handleNextStep(currentStepId) {
            // Validate current step before proceeding
            if (!this.getStepValidation(currentStepId)) {
                console.warn('Step validation failed for step:', currentStepId)
                this.$toast.error('Veuillez compléter tous les champs requis avant de continuer.')
                return
            }

            // Mark current step as completed
            if (!this.completedSteps.includes(currentStepId)) {
                this.completedSteps.push(currentStepId)
            }

            // NOTE: Each step component now calls saveTreatment() before emitting 'next-step'
            // (as part of the API validation implementation)
            // We just need to update the URL if this is the first save (transitioning from create to create/{id})

            // Update browser URL to enable resume on refresh
            if (this.treatmentId && !this.id) {
                const newUrl = `/back-office/v3/treatment/create/${this.treatmentId}`
                window.history.replaceState({ treatmentId: this.treatmentId }, '', newUrl)
            }

            const nextStepId = currentStepId + 1
            this.goToStep(nextStepId)

            // Update highest validated step
            if (nextStepId > this.highestValidatedStep) {
                this.highestValidatedStep = nextStepId
            }
        },

        async saveCurrentProgress(stepNumber) {
            if (this.isSaving) return

            this.isSaving = true
            try {
                await this.saveStepProgress(stepNumber)
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.isSaving = false
            }
        },

        goToStep(nextStep) {
            setTimeout(() => {
                this.step = nextStep
                this.setCurrentStep(nextStep)
                this.resetStepBaseline(nextStep)
            }, 300)
        },

        goToPrevious(previousStep) {
            if (previousStep < 1) return
            if (this.hasAnyUnsavedChanges()) {
                this._confirmLeave(() => {
                    const refName = 'step' + this.step
                    const stepComponent = this.$refs[refName]
                    const instance = Array.isArray(stepComponent) ? stepComponent[0] : stepComponent
                    if (instance && typeof instance.revertChanges === 'function') {
                        instance.revertChanges()
                    }
                    setTimeout(() => {
                        this.step = previousStep
                        this.setCurrentStep(previousStep)
                        this.resetStepBaseline(previousStep)
                    }, 300)
                })
            } else {
                setTimeout(() => {
                    this.step = previousStep
                    this.setCurrentStep(previousStep)
                    this.resetStepBaseline(previousStep)
                }, 300)
            }
        },

        handleStepClick(targetStep) {
            if (targetStep === this.step) return

            const refName = 'step' + this.step
            const stepComponent = this.$refs[refName]
            const instance = Array.isArray(stepComponent) ? stepComponent[0] : stepComponent

            if (instance && typeof instance.hasChanges === 'function' && instance.hasChanges()) {
                const t = targetStep
                this._confirmLeave(() => {
                    if (typeof instance.revertChanges === 'function') {
                        instance.revertChanges()
                    }
                    this.executeStepChange(t)
                })
                return
            }

            this.executeStepChange(targetStep)
        },

        async handleSaveAndExit() {
            const refName = 'step' + this.step
            const stepComponent = this.$refs[refName]

            if (!stepComponent) {
                console.warn(`⚠️ Ref ${refName} not found`)
                return
            }

            const instance = Array.isArray(stepComponent) ? stepComponent[0] : stepComponent

            if (typeof instance.saveStep === 'function') {
                await instance.saveStep(true)
            } else {
                try {
                    await this.saveCurrentProgress(this.step)
                    this.redirectToList()
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                    console.error('Error saving treatment:', error)
                }
            }
        },

        redirectToList() {
            const baseUrl = `${window.location.protocol}//${window.location.host}`
            window.location.href = `${baseUrl}/back-office/v3/experience`
        },

        hasAnyUnsavedChanges() {
            if (this._forceExit) return false
            const refName = 'step' + this.step
            const comp = this.$refs[refName]
            const instance = Array.isArray(comp) ? comp[0] : comp
            return !!(instance && typeof instance.hasChanges === 'function' && instance.hasChanges())
        },

        handleRequestNavigation(navEvent) {
            if (this.hasAnyUnsavedChanges()) {
                navEvent.defaultPrevented = true
            }
        },

        _handleForceNavigation() {
            if (this._beforeUnloadHandler) {
                window.removeEventListener('beforeunload', this._beforeUnloadHandler)
            }
            this._forceExit = true
        },

        executeStepChange(targetStep) {
            if (this.complited || targetStep <= this.highestValidatedStep) {
                this.step = targetStep
                this.setCurrentStep(targetStep)
                this.$nextTick(() => {
                    this.resetStepBaseline(targetStep)
                })
            } else {
                this.$toast.info(this.$t('treatment.notifications.validationStepFirst'))
            }
        },

        resetStepBaseline(stepId) {
            const refName = 'step' + stepId
            const stepComponent = this.$refs[refName]
            const instance = Array.isArray(stepComponent) ? stepComponent[0] : stepComponent
            if (instance && typeof instance.getFormDataString === 'function') {
                instance.initialDataString = instance.getFormDataString()
            }
        },
    },
    mounted() {
        this._beforeUnloadHandler = (e) => {
            if (this.hasAnyUnsavedChanges()) {
                e.preventDefault()
                e.returnValue = ''
            }
        }
        window.addEventListener('beforeunload', this._beforeUnloadHandler)

        this._turboBeforeVisitHandler = (e) => {
            if (this.hasAnyUnsavedChanges()) {
                e.preventDefault()
                const url = e.detail.url
                this._confirmLeave(() => {
                    this._forceExit = true
                    if (this._beforeUnloadHandler) {
                        window.removeEventListener('beforeunload', this._beforeUnloadHandler)
                    }
                    if (window.Turbo) {
                        window.Turbo.visit(url)
                    } else {
                        window.location.href = url
                    }
                })
            }
        }
        document.addEventListener('turbo:before-visit', this._turboBeforeVisitHandler)

        EventBus.$on('request-navigation', this.handleRequestNavigation)
        EventBus.$on('force-navigation', this._handleForceNavigation)
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this._beforeUnloadHandler)
        document.removeEventListener('turbo:before-visit', this._turboBeforeVisitHandler)
        EventBus.$off('request-navigation', this.handleRequestNavigation)
        EventBus.$off('force-navigation', this._handleForceNavigation)
    },
}
</script>

<style scoped>
.v-sheet.v-stepper:not(.v-sheet--outlined) {
    box-shadow: none;
    height: 100%;
}

.step_title_content {
    font-family: 'Poppins Light', sans-serif;
    text-align: center;
    margin-bottom: 0;
}
/* Stepper container */
.stepper-container {
    display: flex;
    flex-direction: column;
    position: relative;
    padding-left: 40px;
    border-right: 0.5px solid #5f5fa3;
}

/* Step item */
.step-item {
    position: relative;
    display: flex;
    align-items: flex-start;
    cursor: pointer;
    min-height: 80px;
}
.stepper-container-edit {
    background-color: rgba(0, 0, 0, 0.05);
    border-right: none !important;
    padding-left: 0 !important;
}
.dark .stepper-container-edit {
    background-color: #d9d9d90d;
}
/* Circle base */
.circle {
    position: relative;
    width: 18px;
    min-width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid #021832;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
}
.dark .circle {
    border: 2px solid #fff;
}
/* Ligne verticale */
.line {
    position: absolute;
    left: 10px;
    top: 30px;
    width: 1px;
    height: 40px;
    background: #021832;
    opacity: 0.3;
    z-index: 1;
}
.dark .line {
    background: #fff;
}

/* ✅ Étape terminée */
.step-item.completed .circle {
    background: #06b217;
    border-color: #06b217;
}
.step-item.completed .line {
    background: #06b217;
}

/* 🟣 Étape active */
.step-item.active .circle {
    border-color: #7d2dbd;
    box-shadow: 0 0 8px #7d2dbd;
}

.step-title {
    font-family: 'Roboto Medium';
    text-transform: uppercase;
}

.step-subtitle {
    font-family: 'Roboto Medium';
}

/* Étapes à venir (non actives) */
.step-item:not(.active):not(.completed) .step-title,
.step-item:not(.active):not(.completed) .step-subtitle,
.step-item:not(.active):not(.completed) .circle {
    opacity: 0.3;
}

.step-item.activeMenu {
    border-left: 3px solid;
    border-left-color: #7d2dbd;
    background-color: #1a1f371a;

    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
    max-width: 80%;
}
.dark .step-item.activeMenu {
    background-color: rgba(255, 255, 255, 0.09);
}

.step-item.activeMenu *,
.step-item.completedMenu * {
    opacity: 1 !important;
}

.form-header {
    margin-bottom: 40px;
}

.form-step {
    text-transform: uppercase;
    margin-bottom: 6px;
}

/* use from create */
.step-item.disabled {
    pointer-events: none;
    cursor: not-allowed;
}
</style>
